import { ApiError } from '@frontend/api-utils';
import { EntityType, ExpectedType } from '@frontend/common';
import { DefaultPrimaryButton, DefaultSecondaryButton } from '@frontend/components/common';
import { EnumSelectListV2 } from '@frontend/components/select';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import React, { useState } from 'react';

import { FormField } from './form-field';
import useForm from './form.controller';
import { mergeEntityScopedFieldProps } from './merge-entity-scoped-field-props';

const ID = 'form';
export const defaultObjectFormRowClassName = 'w-full';
export interface FormProps<T extends object, S extends object> {
    id?: string;
    dispatch: ThunkDispatch<any, any, Action>;
    type: EntityType;
    label?: React.ReactNode;
    object?: T;
    value: S;

    /**
     * @description will only show the keys provided in the same order as provided.
     * Is required to make sure undefined values are also rendered.
     * Keys provided in the hidden array will overrule keys provided here.
     *
     * Keys provided in the reqiured prop are merged and therefore do not need to be provided here.
     */
    show?: (keyof S | Array<keyof S>)[];
    /**
     * @description will the keys provided required. Overrules the hidden array.
     * Keys provided here will be merged with the show array. So no need to provide them there again.
     */
    required?: (keyof S | Array<keyof S>)[];
    /**
     * @description will hide the keys provided. If not provided, all keys will be shown.
     */
    hidden?: (keyof S)[];

    /**
     * @description Can be used for keys in the object mapper that dont exist on the object itself.
     */
    objectSelectKeyOverwrite?: Map<keyof S, string>;
    /**
     * @description Keys will be used to populate the labels. This variable allows you to overwrite it.
     */
    keyOverwrite?: Map<keyof S, React.ReactNode>;
    fieldOverwrite?: Map<keyof S, React.ReactNode>;
    fieldProps?: Map<keyof S, any>;
    expectedType?: Map<keyof S, ExpectedType>;
    /**
     * @description can be used to overwrite a key of the object if the key does not match the correct key from the EnumObjectSelectMapper
     * eg. object.type will probable be an enum. but there is no one enum for type, since type is used in multiple objects
     * @example enumOverwrite={new Map([['type', 'entity_type']])}
     */
    enumOverwrite?: Map<keyof S, keyof typeof EnumSelectListV2>;
    successMessageOverwrite?: React.ReactNode;
    errorMessageOverwrite?: (error: ApiError) => React.ReactNode;
    headerOverwrite?: React.ReactNode;

    bodyClassname?: string;
    rowClassName?: string;
    headerClassName?: string;
    cardBodyClassName?: string;
    submitDisabled?: boolean;
    footerClassName?: string;

    children?: React.ReactNode;
    append?: React.ReactNode;

    onChange?: (value: S) => void;
    onSubmit?: (value: S) => Promise<T> | Promise<void>;
    additionalSubmit?: {
        label: React.ReactNode;
        onClick: (value: S) => Promise<T | void>;
        onSubmitSuccess?: (value: T) => void;
        onVoidSuccess?: (value: void) => void;
    };
    onSubmitSuccess?: (value: T) => void;
    onVoidSuccess?: (value: void) => void;
    onCancel?: (e: any) => void;
    formIdCallback?: (id: string) => void;
    useStaticId?: boolean;
    submitButtonOverride?: React.ReactNode;
    subForms?: Map<React.ReactNode, SubFormProps<T, S>>;
    title?: React.ReactNode;
    styles?: {
        submitButton?: string;
        cancelButton?: string;
    };
    footerButton?: React.ReactNode;
}

export const Form = <T extends object, S extends object>(props: FormProps<T, S>) => {
    const viewProps = useForm(props);
    const header = props.headerOverwrite ? props.headerOverwrite : props.type.replace(/_/g, ' ');
    /** Prefer the entity's account for select options when editing a shared (foreign) entity. */
    const scopedFieldProps = (fieldProps?: Record<string, unknown>) => mergeEntityScopedFieldProps(props.object, fieldProps);
    return (
        <form
            id={props.id ?? ID}
            className={props.bodyClassname ? props.bodyClassname : 'card bg-background-surface w-full lg:w-1/2'}
            onSubmit={props.onSubmit ? viewProps.submit : undefined}>
            <div className={props.headerClassName ? props.headerClassName : 'card-header bg-background-surface text-foreground-default'}>
                <div
                    className='w-full'
                    role='heading'>
                    {props.label ? props.label : props.object ? CommonMessage.FORMS.UPDATE_OBJECT(header) : CommonMessage.FORMS.CREATE_OBJECT(header)}
                </div>
            </div>
            <div className='no-scrollbar flex h-full w-full flex-col items-start justify-between self-stretch overflow-auto'>
                <div className='flex w-full flex-col gap-4'>
                    <div className={props.cardBodyClassName ? props.cardBodyClassName : 'card-body flex w-full flex-col flex-wrap justify-between'}>
                        {props.children}

                        <div className='flex w-full flex-col gap-3'>
                            {props.title && <div className='text-foreground-default justify-start text-xl font-semibold'>{props.title}</div>}
                            {viewProps.show.map((key) => {
                                if (Array.isArray(key)) {
                                    return (
                                        <div
                                            key={key.join('_')}
                                            className={viewProps.rowClass + ' flex flex-row gap-2'}>
                                            {key.map((k) => (
                                                <FormField
                                                    key={k as string}
                                                    dispatch={props.dispatch}
                                                    formId={viewProps.formId}
                                                    fieldKey={k}
                                                    label={props.keyOverwrite?.get(k)}
                                                    objectSelectKeyOverwrite={props.objectSelectKeyOverwrite?.get(k)}
                                                    fieldOverwrite={props.fieldOverwrite?.get(k)}
                                                    enumOverwrite={props.enumOverwrite?.get(k)}
                                                    expectedType={props.expectedType?.get(k)}
                                                    fieldProps={scopedFieldProps(props.fieldProps?.get(k))}
                                                    required={
                                                        Array.isArray(props.required)
                                                            ? props.required.some((r) => (Array.isArray(r) ? r.includes(k) : r === k))
                                                            : false
                                                    }
                                                    hidden={props.hidden?.includes(k)}
                                                    className={viewProps.rowClass}
                                                    id={k as string}
                                                />
                                            ))}
                                        </div>
                                    );
                                }
                                return (
                                    <FormField
                                        id={key as string}
                                        dispatch={props.dispatch}
                                        formId={viewProps.formId}
                                        fieldKey={key}
                                        label={props.keyOverwrite?.get(key)}
                                        objectSelectKeyOverwrite={props.objectSelectKeyOverwrite?.get(key)}
                                        fieldOverwrite={props.fieldOverwrite?.get(key)}
                                        enumOverwrite={props.enumOverwrite?.get(key)}
                                        expectedType={props.expectedType?.get(key)}
                                        fieldProps={scopedFieldProps(props.fieldProps?.get(key))}
                                        required={props.required?.includes(key)}
                                        hidden={props.hidden?.includes(key)}
                                        className={viewProps.rowClass}
                                    />
                                );
                            })}
                        </div>

                        {props.append}
                    </div>
                    {props.subForms &&
                        Array.from(props.subForms.entries()).map(([title, subForm]) => {
                            const [isCollapsed, setIsCollapsed] = useState<boolean>(subForm.defaultCollapsed ?? false);
                            const [active, setActive] = useState<React.ReactNode | null>(
                                subForm.orderTitles ? Array.from(subForm.orderTitles.keys())[0] : null
                            );

                            // Skip rendering if show is false, but hooks are still called
                            if (subForm.show === false) {
                                return null;
                            }

                            return (
                                <div
                                    key={`subform-${title}`}
                                    className={
                                        props.cardBodyClassName ? props.cardBodyClassName : 'card-body flex h-fit w-full flex-col flex-wrap justify-between'
                                    }>
                                    <div className='flex w-full flex-col gap-3'>
                                        <div className='flex justify-between'>
                                            <div className='text-foreground-default justify-start text-xl font-semibold'>{title}</div>
                                            {subForm.collapsible && (
                                                <button
                                                    type='button'
                                                    onClick={() => setIsCollapsed(!isCollapsed)}
                                                    className='text-brand-muted hover:text-brand-primary cursor-pointer justify-start text-center text-base font-medium'>
                                                    {isCollapsed ? (
                                                        <ICONS.BUTTON.CHEVRON_DOWN className='size-6' />
                                                    ) : (
                                                        <ICONS.BUTTON.CHEVRON_UP className='size-6' />
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                        {subForm.orderTitles
                                            ? !isCollapsed && (
                                                  <div className='flex flex-col gap-4'>
                                                      <div className='flex gap-2'>
                                                          {Array.from(subForm.orderTitles.entries()).map(([title, keys]) => (
                                                              <button
                                                                  key={title as string}
                                                                  type='button'
                                                                  onClick={(e) => {
                                                                      e.stopPropagation();
                                                                      e.preventDefault();
                                                                      setActive(title);
                                                                  }}
                                                                  className={`border-b-2 px-1 py-2 text-base font-medium ${active === title ? 'text-brand-primary border-brand-primary' : 'text-foreground-default hover:border-foreground-default border-transparent hover:border-b-2'} flex items-center justify-start gap-2 hover:cursor-pointer`}>
                                                                  {title}
                                                              </button>
                                                          ))}
                                                      </div>
                                                      {subForm.orderTitles.get(active)?.map((key) => {
                                                          if (Array.isArray(key)) {
                                                              return (
                                                                  <div
                                                                      key={key.join('_')}
                                                                      className={viewProps.rowClass + ' flex flex-row gap-2'}>
                                                                      {key.map((k) => (
                                                                          <FormField
                                                                              key={k as string}
                                                                              dispatch={props.dispatch}
                                                                              formId={viewProps.formId}
                                                                              fieldKey={k}
                                                                              label={subForm.keyOverwrite?.get(k)}
                                                                              objectSelectKeyOverwrite={subForm.objectSelectKeyOverwrite?.get(k)}
                                                                              fieldOverwrite={subForm.fieldOverwrite?.get(k)}
                                                                              enumOverwrite={subForm.enumOverwrite?.get(k)}
                                                                              expectedType={subForm.expectedType?.get(k)}
                                                                              fieldProps={scopedFieldProps(subForm.fieldProps?.get(k))}
                                                                              required={
                                                                                  Array.isArray(props.required)
                                                                                      ? subForm.required?.some((r) =>
                                                                                            Array.isArray(r) ? r.includes(k) : r === k
                                                                                        )
                                                                                      : false
                                                                              }
                                                                              hidden={subForm.hidden?.includes(k)}
                                                                              className={viewProps.rowClass}
                                                                          />
                                                                      ))}
                                                                  </div>
                                                              );
                                                          }
                                                          return (
                                                              <FormField
                                                                  id={key as string}
                                                                  dispatch={props.dispatch}
                                                                  formId={viewProps.formId}
                                                                  fieldKey={key}
                                                                  label={subForm.keyOverwrite?.get(key)}
                                                                  objectSelectKeyOverwrite={subForm.objectSelectKeyOverwrite?.get(key)}
                                                                  fieldOverwrite={subForm.fieldOverwrite?.get(key)}
                                                                  enumOverwrite={subForm.enumOverwrite?.get(key)}
                                                                  expectedType={subForm.expectedType?.get(key)}
                                                                  fieldProps={scopedFieldProps(subForm.fieldProps?.get(key))}
                                                                  required={subForm.required?.includes(key)}
                                                                  hidden={subForm.hidden?.includes(key)}
                                                                  className={viewProps.rowClass}
                                                              />
                                                          );
                                                      })}
                                                  </div>
                                              )
                                            : subForm.order.map((key) => {
                                                  if (isCollapsed) return null;
                                                  if (Array.isArray(key)) {
                                                      return (
                                                          <div
                                                              key={key.join('_')}
                                                              className={viewProps.rowClass + ' flex flex-row gap-2'}>
                                                              {key.map((k) => (
                                                                  <FormField
                                                                      key={k as string}
                                                                      dispatch={props.dispatch}
                                                                      formId={viewProps.formId}
                                                                      fieldKey={k}
                                                                      label={subForm.keyOverwrite?.get(k)}
                                                                      objectSelectKeyOverwrite={subForm.objectSelectKeyOverwrite?.get(k)}
                                                                      fieldOverwrite={subForm.fieldOverwrite?.get(k)}
                                                                      enumOverwrite={subForm.enumOverwrite?.get(k)}
                                                                      expectedType={subForm.expectedType?.get(k)}
                                                                      fieldProps={scopedFieldProps(subForm.fieldProps?.get(k))}
                                                                      required={
                                                                          Array.isArray(props.required)
                                                                              ? subForm.required?.some((r) => (Array.isArray(r) ? r.includes(k) : r === k))
                                                                              : false
                                                                      }
                                                                      hidden={subForm.hidden?.includes(k)}
                                                                      className={viewProps.rowClass}
                                                                  />
                                                              ))}
                                                          </div>
                                                      );
                                                  }
                                                  return (
                                                      <FormField
                                                          id={key as string}
                                                          dispatch={props.dispatch}
                                                          formId={viewProps.formId}
                                                          fieldKey={key}
                                                          label={subForm.keyOverwrite?.get(key)}
                                                          objectSelectKeyOverwrite={subForm.objectSelectKeyOverwrite?.get(key)}
                                                          fieldOverwrite={subForm.fieldOverwrite?.get(key)}
                                                          enumOverwrite={subForm.enumOverwrite?.get(key)}
                                                          expectedType={subForm.expectedType?.get(key)}
                                                          fieldProps={scopedFieldProps(subForm.fieldProps?.get(key))}
                                                          required={subForm.required?.includes(key)}
                                                          hidden={subForm.hidden?.includes(key)}
                                                          className={viewProps.rowClass}
                                                      />
                                                  );
                                              })}
                                        {subForm.append}
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>
            {props.onSubmit && (
                <div className={props.footerClassName ? props.footerClassName : 'card-footer flex justify-between'}>
                    <div className='flex items-center gap-3'>
                        {props.onCancel ? (
                            <DefaultSecondaryButton
                                onClick={(e) => {
                                    e.preventDefault();
                                    props.onCancel && props.onCancel(e);
                                }}
                                text={CommonMessage.BUTTONS.CANCEL}
                            />
                        ) : null}
                        {props.footerButton}
                    </div>
                    <div className='flex items-center gap-3'>
                        <DefaultPrimaryButton
                            onClick={(e) => viewProps.submit(e)}
                            text={props.submitButtonOverride ? props.submitButtonOverride : CommonMessage.BUTTONS.SUBMIT}
                            disabled={props.submitDisabled}
                        />
                        {props.additionalSubmit && (
                            <DefaultPrimaryButton
                                onClick={(e) => {
                                    viewProps.additionalSubmit(e);
                                }}
                                text={props.additionalSubmit.label}
                                disabled={props.submitDisabled}
                            />
                        )}
                    </div>
                </div>
            )}
        </form>
    );
};

export default Form;

export interface SubFormProps<T extends object, S extends object> extends Omit<Partial<FormProps<T, S>>, 'show'> {
    order: (keyof S | Array<keyof S>)[];
    collapsible?: boolean;
    defaultCollapsed?: boolean;
    orderTitles?: Map<React.ReactNode, (keyof S | Array<keyof S>)[]>;
    show?: boolean;
}
